import { useState } from "react";
import { Download, FileText, Info, Loader2, Upload, X } from "lucide-react";
import Button from "components/ui-custom/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui-custom/Dialog";
import SampleBoQSheet from "assets/sample_sheets/Sample_BoQ.xlsx";
import { BoQApi } from "../../../api/boq/service";
import { toast } from "sonner";

const SUPPORTED_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/csv",
];

const formatValidationErrors = (details = []) => {
  return details
    .slice(0, 3)
    .map((item) => {
      const rowLabel = item?.row ? `Row ${item.row}` : "Row";
      const fieldErrors = Object.entries(item?.errors || {})
        .map(([field, value]) => {
          const message = Array.isArray(value) ? value.join(", ") : String(value);
          return `${field}: ${message}`;
        })
        .join("; ");
      return fieldErrors ? `${rowLabel} - ${fieldErrors}` : rowLabel;
    })
    .join(" | ");
};

const getErrorMessage = (payload) => {
  if (!payload) {
    return "Failed to upload BoQ sheet";
  }

  const rowErrors = Array.isArray(payload?.errors)
    ? payload.errors
    : Array.isArray(payload?.details)
      ? payload.details
      : [];

  if (rowErrors.length > 0) {
    const formattedErrors = formatValidationErrors(rowErrors);
    if (formattedErrors) {
      const hasMoreRows = rowErrors.length > 3;
      return `${payload.message || payload.error || "Validation errors in Excel data"}: ${formattedErrors}${hasMoreRows ? " ..." : ""
        }`;
    }
  }

  return payload.error || payload.message || payload.detail || "Failed to upload BoQ sheet";
};

const getSuccessMessage = (result) => {
  if (result?.message) {
    return result.message;
  }

  const sectionsCreated = Number(result?.sections_created) || 0;
  const itemsCreated = Number(result?.items_created) || 0;

  return `Successfully imported ${sectionsCreated} sections and ${itemsCreated} items`;
};

export default function ImportBoQModal({ open, onClose, onUploadSuccess }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [showInfo, setShowInfo] = useState(false);

  const handleFile = (file) => {
    if (!file) return;

    const fileName = file.name?.toLowerCase() || "";
    const hasValidExtension =
      fileName.endsWith(".xlsx") || fileName.endsWith(".xls") || fileName.endsWith(".csv");
    const hasValidMime = SUPPORTED_TYPES.includes(file.type);

    if (!hasValidExtension && !hasValidMime) {
      const message = "Please upload an Excel file (.xlsx, .xls or .csv)";
      setUploadMessage(message);
      setMessageType("error");
      toast.error(message);
      return;
    }

    setSelectedFile(file);
    setUploadMessage("");
    setMessageType("info");
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    const fileInput = document.getElementById("boq-bulk-upload-file");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      const message = "Please select an Excel sheet first";
      setUploadMessage(message);
      setMessageType("error");
      toast.warning(message);
      return;
    }

    setIsUploading(true);
    setUploadMessage("");

    try {
      const result = await BoQApi.bulkUpload(selectedFile);

      if (result?.success === false) {
        throw new Error(getErrorMessage(result));
      }

      const successMessage = getSuccessMessage(result);
      setUploadMessage(successMessage);
      setMessageType("success");
      toast.success(successMessage);

      if (typeof onUploadSuccess === "function") {
        await onUploadSuccess(result);
      }

      setTimeout(() => {
        onClose?.();
      }, 500);
    } catch (error) {
      const message = getErrorMessage(error?.data) || error?.message || "Failed to upload BoQ sheet";
      setUploadMessage(message);
      setMessageType("error");
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isUploading && onClose?.()}>
      <DialogContent className="sm:max-w-xl p-0">
        <DialogHeader className="flex flex-row items-center justify-between border-b border-border/60 p-2">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-base">Import BoQ From Excel</DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 p-0 text-muted-foreground"
              onClick={() => setShowInfo((prev) => !prev)}
              disabled={isUploading}
              aria-label="Show import instructions"
            >
              <Info className="h-4 w-4" />
            </Button>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 p-0"
            onClick={onClose}
            disabled={isUploading}
            aria-label="Close import modal"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="space-y-4 px-4 py-4">
          {showInfo && (
            <div className="rounded-md bg-blue-50 p-4 text-sm text-blue-700">
              <ul className="list-disc space-y-1 pl-5">
                <li>First download the sample sheet.</li>
                <li>Do not change the column names.</li>
                <li>Enter BoQ sections and items row by row.</li>
                <li>Save the updated sheet and upload it here.</li>
              </ul>
            </div>
          )}

          {!selectedFile ? (
            <div
              className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${dragActive ? "border-sm-primary bg-sm-primary/5" : "border-border"
                }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                id="boq-bulk-upload-file"
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                onChange={(event) => handleFile(event.target.files?.[0])}
                disabled={isUploading}
              />
              <label
                htmlFor="boq-bulk-upload-file"
                className={`flex flex-col items-center gap-2 ${isUploading ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                  }`}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-foreground">Click to upload or drag and drop</span>
                <span className="text-xs text-muted-foreground">Supports .xlsx, .xls and .csv</span>
              </label>
            </div>
          ) : (
            <div className="rounded-lg border border-border p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <FileText className="h-5 w-5 shrink-0 text-sm-primary" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground" title={selectedFile.name}>
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                  onClick={removeSelectedFile}
                  disabled={isUploading}
                  aria-label="Remove selected file"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <a
            href={SampleBoQSheet}
            download
            className="inline-flex items-center gap-1.5 text-sm font-medium text-sm-primary hover:underline"
          >
            <Download className="h-4 w-4" />
            Download Sample
          </a>

          {uploadMessage && (
            <div
              className={`rounded-lg p-3 text-sm ${messageType === "error"
                ? "bg-red-50 text-red-700"
                : messageType === "success"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-blue-50 text-blue-700"
                }`}
            >
              {uploadMessage}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <Button
              variant="outline"
              className="h-9 text-sm"
              onClick={onClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            {selectedFile && (
              <Button
                className="h-9 gap-2 bg-sm-primary text-sm text-white"
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  "Upload Sheet"
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
